import { FieldType } from '../../../../form-engine/parsing/parser';
import type { FormFieldDef } from '../../../../form-engine/types';

/**
 * RAG Value Transformers
 *
 * These functions transform flat RAG extraction values into the nested
 * structures expected by complex dynamic form field types.
 */

// ============================================================================
// Types
// ============================================================================

type CheckboxOption = {
  label: string;
  value: string | number;
  commentEligibility?: string;
};

type CheckboxGroupingValue = {
  values: Array<{ value: string | number }>;
  comment?: string;
};

type ItemizedPercentileListValue = {
  props: Record<string, { value: string; comment?: string }>;
};

type GroupedPercentileListValue = {
  values: Array<{
    props: Record<string, { value: number; comment?: string }>;
  }>;
};

type JobListValue = {
  values: Array<{
    props: {
      client: { value: string };
      date: { value: string };
      natureOfServices: { value: string };
      revenue: { value: string };
      percentGrossRevenue: { value: string };
    };
  }>;
};

type FieldListValue = {
  values: Array<{
    props: Record<string, { value: string | number }>;
  }>;
};

// ============================================================================
// Main Transformer
// ============================================================================

/**
 * Transform RAG raw_value to the format expected by the form field
 *
 * @param rawValue - The raw value from RAG extraction
 * @param fieldType - The type of form field
 * @param fieldMeta - Optional field metadata (needed for some field types)
 * @returns Transformed value ready for form.setValue()
 */
export function transformRagValueForFieldType(
  rawValue: unknown,
  fieldType: FieldType,
  fieldMeta?: FormFieldDef
): unknown {
  switch (fieldType) {
    case FieldType.CheckboxGrouping:
    case FieldType.MultiSelect:
      return transformCheckboxGroupingValue(rawValue, fieldMeta);

    case FieldType.ItemizedPercentileList:
      return transformItemizedPercentileListValue(rawValue);

    case FieldType.GroupedPercentileList:
      return transformGroupedPercentileListValue(rawValue);

    case FieldType.JobList:
      return transformJobListValue(rawValue);

    case FieldType.FieldList:
      return transformFieldListValue(rawValue, fieldMeta);

    default:
      // Simple field types - return as-is
      return rawValue;
  }
}

/**
 * Check if a field type requires transformation
 */
export function requiresTransformation(fieldType: FieldType): boolean {
  return [
    FieldType.CheckboxGrouping,
    FieldType.MultiSelect,
    FieldType.ItemizedPercentileList,
    FieldType.GroupedPercentileList,
    FieldType.JobList,
    FieldType.FieldList,
  ].includes(fieldType);
}

// ============================================================================
// Individual Transformers
// ============================================================================

/**
 * Check if a value is already in the CheckboxGrouping form shape: { values: Array, comment?: string }
 */
function isAlreadyCheckboxGroupingShape(
  value: unknown
): value is CheckboxGroupingValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    'values' in value &&
    Array.isArray((value as CheckboxGroupingValue).values)
  );
}

/**
 * Check if a value is already in the { values: Array } top-level shape (JobList, FieldList, GroupedPercentileList)
 */
function isAlreadyValuesArrayShape(
  value: unknown
): value is { values: unknown[] } {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    'values' in value &&
    Array.isArray((value as { values: unknown }).values)
  );
}

/**
 * Check if a value is already in the ItemizedPercentileList shape: { props: Record<string, {value, ...}> }
 */
function isAlreadyItemizedPercentileListShape(
  value: unknown
): value is ItemizedPercentileListValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    'props' in value &&
    typeof (value as ItemizedPercentileListValue).props === 'object' &&
    !Array.isArray((value as ItemizedPercentileListValue).props)
  );
}

/**
 * Transform CheckboxGrouping / MultiSelect values
 *
 * RAG returns: ["opt1", "opt3"] (array of selected values)
 * Form expects: { values: [{ value: "opt1" }, { value: "" }, { value: "opt3" }], comment?: string }
 *
 * The values array must be aligned with options - each slot corresponds to an option,
 * with selected options having { value: optionValue } and unselected having { value: "" }
 */
function transformCheckboxGroupingValue(
  rawValue: unknown,
  fieldMeta?: FormFieldDef
): CheckboxGroupingValue {
  // Default empty structure
  const defaultValue: CheckboxGroupingValue = { values: [], comment: '' };

  // If already in the expected form shape, return as-is
  if (isAlreadyCheckboxGroupingShape(rawValue)) {
    return rawValue;
  }

  if (!Array.isArray(rawValue)) {
    // If single primitive value, wrap in array
    if (rawValue !== null && rawValue !== undefined) {
      return transformCheckboxGroupingValue([rawValue], fieldMeta);
    }
    return defaultValue;
  }

  // Get options from field metadata (cast to any since valueOptions exists on checkbox/select types)
  const options: CheckboxOption[] =
    (fieldMeta as unknown as { valueOptions?: CheckboxOption[] })
      ?.valueOptions ?? [];

  if (options.length === 0) {
    // No options to align with - return simple mapping
    return {
      values: rawValue.map((val) => ({ value: val })),
      comment: '',
    };
  }

  // Create fixed-length array aligned with options
  const values: Array<{ value: string | number }> = options.map((option) => {
    const isSelected = rawValue.some(
      (selected) => String(selected) === String(option.value)
    );
    return { value: isSelected ? option.value : '' };
  });

  return { values, comment: '' };
}

/**
 * Transform ItemizedPercentileList values
 *
 * RAG returns: { key1: 50, key2: 30 } OR { key1: { value: "50", comment: "..." } }
 * Form expects: { props: { key1: { value: "50", comment?: "..." }, key2: { value: "30" } } }
 */
function transformItemizedPercentileListValue(
  rawValue: unknown
): ItemizedPercentileListValue {
  const defaultValue: ItemizedPercentileListValue = { props: {} };

  if (!rawValue || typeof rawValue !== 'object' || Array.isArray(rawValue)) {
    return defaultValue;
  }

  // If already in the expected form shape { props: {...} }, return as-is
  if (isAlreadyItemizedPercentileListShape(rawValue)) {
    return rawValue;
  }

  const props: Record<string, { value: string; comment?: string }> = {};

  for (const [key, val] of Object.entries(
    rawValue as Record<string, unknown>
  )) {
    if (val && typeof val === 'object' && 'value' in val) {
      // Already in correct format: { value: "...", comment?: "..." }
      const objVal = val as { value: unknown; comment?: string };
      props[key] = {
        value: String(objVal.value ?? ''),
        ...(objVal.comment && { comment: objVal.comment }),
      };
    } else {
      // Simple value - wrap it
      props[key] = { value: String(val ?? '') };
    }
  }

  return { props };
}

/**
 * Transform GroupedPercentileList values
 *
 * RAG returns: [{ key1: 50, key2: 30 }, { key3: 20 }] (array of sections)
 * Form expects: { values: [{ props: { key1: { value: 50 }, key2: { value: 30 } } }, ...] }
 */
function transformGroupedPercentileListValue(
  rawValue: unknown
): GroupedPercentileListValue {
  const defaultValue: GroupedPercentileListValue = { values: [] };

  // If already in the expected form shape { values: [...] }, return as-is
  if (isAlreadyValuesArrayShape(rawValue)) {
    return rawValue as GroupedPercentileListValue;
  }

  if (!Array.isArray(rawValue)) {
    // Single plain object (not { values: [...] }) - treat as a single section
    if (rawValue && typeof rawValue === 'object') {
      return transformGroupedPercentileListValue([rawValue]);
    }
    return defaultValue;
  }

  const values = rawValue.map((section) => {
    if (!section || typeof section !== 'object') {
      return { props: {} };
    }

    const props: Record<string, { value: number; comment?: string }> = {};

    for (const [key, val] of Object.entries(
      section as Record<string, unknown>
    )) {
      if (val && typeof val === 'object' && 'value' in val) {
        // Already in correct format
        const objVal = val as { value: unknown; comment?: string };
        props[key] = {
          value: Number(objVal.value) || 0,
          ...(objVal.comment && { comment: objVal.comment }),
        };
      } else {
        // Simple value - wrap it
        props[key] = { value: Number(val) || 0 };
      }
    }

    return { props };
  });

  return { values };
}

/**
 * Transform JobList values
 *
 * RAG returns: [{ client: "Acme", date: "2024-01-15", natureOfServices: "Consulting", revenue: "50000", percentGrossRevenue: "25" }]
 * Form expects: { values: [{ props: { client: { value: "Acme" }, date: { value: "2024-01-15" }, ... } }] }
 */
function transformJobListValue(rawValue: unknown): JobListValue {
  const defaultValue: JobListValue = { values: [] };

  // If already in the expected form shape { values: [...] }, return as-is
  if (isAlreadyValuesArrayShape(rawValue)) {
    return rawValue as JobListValue;
  }

  if (!Array.isArray(rawValue)) {
    // Single plain object (not { values: [...] }) - treat as a single job
    if (rawValue && typeof rawValue === 'object') {
      return transformJobListValue([rawValue]);
    }
    return defaultValue;
  }

  const JOB_FIELDS = [
    'client',
    'date',
    'natureOfServices',
    'revenue',
    'percentGrossRevenue',
  ] as const;

  const values = rawValue.map((job) => {
    if (!job || typeof job !== 'object') {
      return {
        props: {
          client: { value: '' },
          date: { value: '' },
          natureOfServices: { value: '' },
          revenue: { value: '' },
          percentGrossRevenue: { value: '' },
        },
      };
    }

    const jobObj = job as Record<string, unknown>;
    const props: JobListValue['values'][number]['props'] = {
      client: { value: '' },
      date: { value: '' },
      natureOfServices: { value: '' },
      revenue: { value: '' },
      percentGrossRevenue: { value: '' },
    };

    for (const field of JOB_FIELDS) {
      const val = jobObj[field];
      if (val && typeof val === 'object' && 'value' in val) {
        // Already wrapped
        props[field] = {
          value: String((val as { value: unknown }).value ?? ''),
        };
      } else if (val !== undefined && val !== null) {
        // Wrap the value
        props[field] = { value: String(val) };
      }
    }

    return { props };
  });

  return { values };
}

/**
 * Transform FieldList values
 *
 * RAG returns: [{ fieldKey1: "val1", fieldKey2: 123 }]
 * Form expects: { values: [{ props: { fieldKey1: { value: "val1" }, fieldKey2: { value: 123 } } }] }
 */
function transformFieldListValue(
  rawValue: unknown,
  fieldMeta?: FormFieldDef
): FieldListValue {
  const defaultValue: FieldListValue = { values: [] };

  // If already in the expected form shape { values: [...] }, return as-is
  if (isAlreadyValuesArrayShape(rawValue)) {
    return rawValue as FieldListValue;
  }

  if (!Array.isArray(rawValue)) {
    // Single plain object (not { values: [...] }) - treat as a single item
    if (rawValue && typeof rawValue === 'object') {
      return transformFieldListValue([rawValue], fieldMeta);
    }
    return defaultValue;
  }

  // Get required field keys from schema if available
  const schemaProps = fieldMeta?.schema?.properties?.values?.items;
  const schemaRequired = Array.isArray(schemaProps)
    ? schemaProps[0]?.properties?.props?.required
    : undefined;
  const requiredKeys: string[] = Array.isArray(schemaRequired)
    ? schemaRequired
    : [];

  const values = rawValue.map((item) => {
    if (!item || typeof item !== 'object') {
      return { props: {} };
    }

    const itemObj = item as Record<string, unknown>;
    const props: Record<string, { value: string | number }> = {};

    // If we have schema, use those keys; otherwise use all keys from the item
    const keysToProcess =
      requiredKeys.length > 0 ? requiredKeys : Object.keys(itemObj);

    for (const key of keysToProcess) {
      const val = itemObj[key];
      if (val && typeof val === 'object' && 'value' in val) {
        // Already wrapped
        props[key] = { value: (val as { value: string | number }).value };
      } else if (val !== undefined && val !== null) {
        // Wrap the value - preserve type (string or number)
        props[key] = {
          value: typeof val === 'number' ? val : String(val),
        };
      } else {
        // Default empty value
        props[key] = { value: '' };
      }
    }

    return { props };
  });

  return { values };
}
